import React from "react";
import { LifeBuoy, Plus, MessageSquare, Loader2, Send } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, SkeletonRows, Card } from "@/components/dash/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { format } from "date-fns";
import { toast } from "sonner";

export default function Support() {
  const { user } = useAuth();
  const [tickets, setTickets] = React.useState(null);
  const [showCreate, setShowCreate] = React.useState(false);
  const [detail, setDetail] = React.useState(null);
  const [form, setForm] = React.useState({ subject: "", category: "General Query", priority: "Medium", description: "" });
  const [reply, setReply] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setTickets(null);
    try { const { data } = await api.get("/support-tickets", { params: { page_size: 50 } }); setTickets(data.items); } catch { setTickets([]); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.subject) return toast.error("Subject is required");
    setBusy(true);
    try { await api.post("/support-tickets", form); toast.success("Ticket created"); setShowCreate(false); setForm({ subject: "", category: "General Query", priority: "Medium", description: "" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/support-tickets/${detail.id}/reply`, { message: reply }); setDetail(data); setReply(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const setStatus = async (s) => { try { const { data } = await api.patch(`/support-tickets/${detail.id}`, { status: s }); setDetail(data); load(); } catch {} };

  return (
    <div>
      <PageHeader title="Help & Support" description="Raise and track support requests." breadcrumb="Workspace / Support">
        <Button onClick={() => setShowCreate(true)} className="bg-vs-royal text-white" data-testid="support-create-btn"><Plus className="h-4 w-4 mr-1.5" />New Ticket</Button>
      </PageHeader>
      {tickets === null ? <SkeletonRows /> : tickets.length === 0 ? (
        <EmptyState icon="LifeBuoy" title="No support tickets" description="Create a ticket and our team will assist you." action={<Button onClick={() => setShowCreate(true)} className="bg-vs-royal text-white">New Ticket</Button>} />
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <button key={t.id} onClick={() => setDetail(t)} data-testid={`ticket-${t.id}`} className="vs-card vs-card-hover w-full text-left p-4 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-lg bg-vs-light flex items-center justify-center shrink-0"><MessageSquare className="h-4 w-4 text-vs-royal" /></div>
                <div className="min-w-0"><div className="font-medium text-slate-800 truncate">{t.subject}</div>
                  <div className="text-xs text-slate-500">{t.ref ? <span className="font-mono text-vs-royal mr-1.5">{t.ref}</span> : null}{t.category} · {t.priority} · {t.created_at ? format(new Date(t.created_at), "dd MMM yyyy") : ""}</div></div>
              </div>
              <StatusBadge status={t.status} />
            </button>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white" data-testid="support-create-modal">
          <DialogHeader><DialogTitle className="font-display">New Support Ticket</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" data-testid="ticket-subject" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="mt-1.5 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{["General Query", "Technical Support", "Account & Login", "Profile & Verification", "Requirement", "Procurement", "Staffing", "Documents", "Payments", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger className="mt-1.5 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{["Low", "Medium", "High", "Urgent"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={4} data-testid="ticket-description" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={create} disabled={busy} className="bg-vs-royal text-white" data-testid="ticket-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Ticket"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="bg-white w-full sm:max-w-lg overflow-y-auto">
          {detail && <>
            <SheetHeader><SheetTitle className="font-display">{detail.subject}</SheetTitle></SheetHeader>
            <div className="mt-4 flex items-center gap-2 flex-wrap">{detail.ref && <span className="font-mono text-xs font-bold text-vs-royal bg-vs-light rounded px-2 py-1">{detail.ref}</span>}<StatusBadge status={detail.status} /><span className="text-xs text-slate-500">{detail.category} · {detail.priority}</span></div>
            <p className="text-sm text-slate-700 mt-4">{detail.description}</p>
            <div className="mt-6 space-y-3">
              {(detail.replies || []).map((r, i) => (
                <div key={i} className={`rounded-lg p-3 text-sm ${r.role === "super_admin" ? "bg-vs-light" : "bg-slate-50"}`}>
                  <div className="font-medium text-slate-800 text-xs mb-1">{r.by} · <span className="capitalize">{r.role?.replace("_", " ")}</span></div>{r.message}</div>
              ))}
            </div>
            {detail.status !== "closed" && (
              <div className="mt-4 flex gap-2">
                <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type a reply…" data-testid="ticket-reply-input" />
                <Button onClick={sendReply} disabled={busy} className="bg-vs-royal text-white" data-testid="ticket-reply-send"><Send className="h-4 w-4" /></Button>
              </div>
            )}
            <div className="mt-4 flex gap-2">
              {user.role === "super_admin" && detail.status !== "resolved" && <Button size="sm" variant="outline" onClick={() => setStatus("resolved")}>Mark Resolved</Button>}
              {detail.status !== "closed" && <Button size="sm" variant="outline" onClick={() => setStatus("closed")} className="text-rose-600 border-rose-200">Close Ticket</Button>}
            </div>
          </>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
